/**
 * Playground skills hook + API client.
 *
 * Wraps ``/v1/playground/skills`` so the management UI + skill
 * selector can list / create / version / promote / soft-delete
 * skills without each component reimplementing the fetch layer.
 *
 * Pattern matches :file:`use-catalog.ts` — plain ``useState`` +
 * ``useEffect``, no external state library. The fetcher uses raw
 * ``fetch`` to stay inside the playground module's existing
 * footprint; no new dependencies.
 */

import { useCallback, useEffect, useState } from "react";
import Cookies from "js-cookie";

import { API_URL } from "@/api/client";
import { COOKIE_KEYS } from "@/lib/constants";

export interface SkillVersion {
  version: number;
  system_prompt: string;
  model: string | null;
  parameters: Record<string, unknown> | null;
  notes: string | null;
  created_at: string;
  created_by_user_id: string | null;
}

export interface Skill {
  id: string;
  workspace_id: string;
  name: string;
  slug: string;
  description: string | null;
  current_version: number | null;
  created_at: string;
  updated_at: string;
  deleted_at: string | null;
  current_version_snapshot: SkillVersion | null;
}

export interface CreateSkillPayload {
  name: string;
  slug?: string;
  description?: string | null;
  system_prompt: string;
  model?: string | null;
  parameters?: Record<string, unknown> | null;
  notes?: string | null;
}

export interface AppendVersionPayload {
  system_prompt: string;
  model?: string | null;
  parameters?: Record<string, unknown> | null;
  notes?: string | null;
}

export interface UpdateSkillPayload {
  name?: string;
  description?: string | null;
}

// API_URL is empty in dev (Vite proxy rewrites) and the gateway origin
// in staging / production. The bare relative path resolved to the
// static-asset origin in prod and nginx returned 405.
const BASE = `${API_URL}/v1/playground/skills`;

function authHeaders(extra: Record<string, string> = {}): Record<string, string> {
  const token = Cookies.get(COOKIE_KEYS.ACCESS_TOKEN);
  const headers: Record<string, string> = {
    Accept: "application/json",
    "Content-Type": "application/json",
    ...extra,
  };
  if (token) headers["Authorization"] = `Bearer ${token}`;
  return headers;
}

async function unwrap<T>(response: Response): Promise<T> {
  if (response.ok) {
    if (response.status === 204) return undefined as T;
    return (await response.json()) as T;
  }
  // Use the structured envelope when present so the UI can branch
  // on ``error`` codes (SKILL_SLUG_CONFLICT, SKILL_VERSION_NOT_FOUND, …).
  const contentType = response.headers.get("content-type") ?? "";
  if (contentType.includes("application/json")) {
    try {
      const body = (await response.json()) as { error?: string; message?: string };
      const message = body.message ?? `HTTP ${response.status}`;
      const err = new Error(message) as Error & { code?: string; status?: number };
      err.code = body.error;
      err.status = response.status;
      throw err;
    } catch (parseErr) {
      if (parseErr instanceof Error && "code" in parseErr) throw parseErr;
      // fall through
    }
  }
  throw new Error(`HTTP ${response.status}`);
}

export const SkillsApi = {
  async list(): Promise<Skill[]> {
    const resp = await fetch(BASE, { method: "GET", headers: authHeaders() });
    const body = await unwrap<{ items: Skill[] }>(resp);
    return body.items;
  },
  async getDetail(skillId: string): Promise<Skill> {
    const resp = await fetch(`${BASE}/${skillId}`, {
      method: "GET",
      headers: authHeaders(),
    });
    return unwrap<Skill>(resp);
  },
  async listVersions(skillId: string): Promise<{ skill: Skill; versions: SkillVersion[] }> {
    const resp = await fetch(`${BASE}/${skillId}/versions`, {
      method: "GET",
      headers: authHeaders(),
    });
    return unwrap(resp);
  },
  async create(payload: CreateSkillPayload): Promise<Skill> {
    const resp = await fetch(BASE, {
      method: "POST",
      headers: authHeaders(),
      body: JSON.stringify(payload),
    });
    return unwrap<Skill>(resp);
  },
  async appendVersion(
    skillId: string,
    payload: AppendVersionPayload
  ): Promise<SkillVersion> {
    const resp = await fetch(`${BASE}/${skillId}/versions`, {
      method: "POST",
      headers: authHeaders(),
      body: JSON.stringify(payload),
    });
    return unwrap<SkillVersion>(resp);
  },
  async update(skillId: string, payload: UpdateSkillPayload): Promise<Skill> {
    const resp = await fetch(`${BASE}/${skillId}`, {
      method: "PATCH",
      headers: authHeaders(),
      body: JSON.stringify(payload),
    });
    return unwrap<Skill>(resp);
  },
  async setCurrentVersion(skillId: string, version: number): Promise<Skill> {
    const resp = await fetch(`${BASE}/${skillId}/current`, {
      method: "PUT",
      headers: authHeaders(),
      body: JSON.stringify({ version }),
    });
    return unwrap<Skill>(resp);
  },
  async remove(skillId: string): Promise<void> {
    const resp = await fetch(`${BASE}/${skillId}`, {
      method: "DELETE",
      headers: authHeaders(),
    });
    if (!resp.ok && resp.status !== 204) {
      await unwrap(resp);
    }
  },
};

export interface UsePlaygroundSkills {
  skills: Skill[];
  loading: boolean;
  error: string | null;
  refetch(): void;
}

export function usePlaygroundSkills(): UsePlaygroundSkills {
  const [skills, setSkills] = useState<Skill[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    SkillsApi.list()
      .then((items) => setSkills(items))
      .catch((err: unknown) => {
        setError(err instanceof Error ? err.message : String(err));
      })
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  return { skills, loading, error, refetch: load };
}

// Templates live in :file:`skill-templates.ts` (Sesi 2b.1) — the
// /skills page exposes a picker that prefills the create form
// from one of five rich AI-governance skill specs.
